// A02 — Admin Dashboard.
// Reference: TVP2 §9 image2.
// Layout: ONE bordered card containing Event + Client tables side-by-side.
// No h1 title (the tab strip already labels the page); no search section
// (per user feedback — removed).
//
// "Event" panel surfaces the latest jobs (newest first) — gives admins a
// quick activity pulse without having to open the Jobs tab. Earlier
// iterations of this page surfaced tickets here; we swapped to jobs
// because tickets are low-volume by design (support requests only) while
// jobs are the app's primary activity signal.

import {
  Box,
  Link as MuiLink,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { tokens } from "@findgeeks/ui-tokens";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { usePagedQuery } from "@/hooks/usePagedQuery";

interface AdminJob {
  id: string;
  title: string;
  description: string | null;
  status: string;
  customer_id: string;
  customer?: { id: string; display_name: string | null } | null;
  created_at: string;
}

interface AdminAppUser {
  id: string;
  display_name: string | null;
  email: string | null;
  role: string;
  created_at: string;
  is_active: boolean;
}

export function Dashboard() {
  const navigate = useNavigate();

  // Latest jobs (created_at desc — natural order returned by /v1/admin/jobs).
  const jobs = usePagedQuery<AdminJob>("jobs", {
    initialPagination: { page: 0, pageSize: 5 },
  });

  const customers = usePagedQuery<AdminAppUser>("app-users", {
    initialPagination: { page: 0, pageSize: 5 },
    filters: { role: "customer" },
  });

  // Title cells render as teal clickable links (matches reference's
  // teal-blue link styling for the leftmost column of each table).
  const linkSx = {
    color: tokens.colors.admin.primary,
    fontWeight: tokens.font.weights.regular,
    cursor: "pointer",
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  } as const;

  const jobColumns: DataTableColumn<AdminJob>[] = [
    {
      field: "title",
      headerName: "Title",
      render: (r) => (
        <MuiLink sx={linkSx} onClick={() => navigate(`/app/jobs/${r.id}`)}>
          {r.title || "(untitled)"}
        </MuiLink>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      render: (r) =>
        r.description
          ? r.description.length > 60
            ? r.description.slice(0, 60) + "..."
            : r.description
          : "-",
    },
    {
      field: "customer",
      headerName: "User",
      render: (r) => r.customer?.display_name ?? "-",
    },
  ];

  const customerColumns: DataTableColumn<AdminAppUser>[] = [
    {
      field: "display_name",
      headerName: "Title",
      render: (r) => (
        <MuiLink sx={linkSx} onClick={() => navigate(`/app/app-users/${r.id}`)}>
          {r.display_name ?? "-"}
        </MuiLink>
      ),
    },
    {
      field: "email",
      headerName: "Desc",
      render: (r) => r.email ?? "-",
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Single bordered card containing BOTH Event + Client tables.
          Reference TVP2 §9 image2: tables sit in one container divided
          by a thin vertical line; each side has its own header label
          and its own pagination footer. */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${tokens.colors.admin.border}`,
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }}>
          {/* Left half — Event (tickets) */}
          <Box sx={{ flex: 1, minWidth: 0, p: 2 }}>
            <Typography
              sx={{
                fontSize: tokens.font.sizes.bodySm,
                fontWeight: tokens.font.weights.semibold,
                color: tokens.colors.admin.textBody,
                mb: 1,
              }}
            >
              Event
            </Typography>
            <DataTable
              columns={jobColumns}
              rows={jobs.data?.items ?? []}
              rowCount={jobs.data?.total ?? 0}
              paginationModel={jobs.paginationModel}
              onPaginationModelChange={jobs.setPaginationModel}
              getRowId={(r) => r.id}
              loading={jobs.isLoading}
              bordered={false}
            />
          </Box>

          {/* Right half — Client (customers) */}
          <Box sx={{ flex: 1, minWidth: 0, p: 2 }}>
            <Typography
              sx={{
                fontSize: tokens.font.sizes.bodySm,
                fontWeight: tokens.font.weights.semibold,
                color: tokens.colors.admin.textBody,
                mb: 1,
              }}
            >
              Client
            </Typography>
            <DataTable
              columns={customerColumns}
              rows={customers.data?.items ?? []}
              rowCount={customers.data?.total ?? 0}
              paginationModel={customers.paginationModel}
              onPaginationModelChange={customers.setPaginationModel}
              getRowId={(r) => r.id}
              loading={customers.isLoading}
              bordered={false}
            />
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
